import { unstable_cache } from "next/cache";
import { ApiFilterResponse } from "@interfaces/filter.types";
import { CACHE_REVALIDATION, CACHE_TAGS } from "@constants/cacheConfig";

const baseURL = process.env.NEXT_PUBLIC_ACKO_DRIVE_CATALOG_SERVICE;

interface FetchFiltersOptions {
  throwOnClientError?: boolean;
}

export class FilterServiceHttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "FilterServiceHttpError";
    this.status = status;
  }
}

export const isFilterClientError = (
  error: unknown
): error is FilterServiceHttpError => {
  return (
    error instanceof FilterServiceHttpError &&
    error.status >= 400 &&
    error.status < 500
  );
};

export const fetchFiltersFromAPI = async (
  collectionSlug?: string,
  options: FetchFiltersOptions = {}
): Promise<ApiFilterResponse | null> => {
  try {
    const queryParams = new URLSearchParams();

    if (collectionSlug) {
      const decodedSlug = decodeURIComponent(collectionSlug);
      queryParams.append("slug", decodedSlug);
    }

    const apiUrl = `${baseURL}/v1/filters/?${queryParams.toString()}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new FilterServiceHttpError(
        response.status,
        `API returned status ${response.status}`
      );
    }

    const data = await response.json();

    return data;
  } catch (error: any) {
    if (options.throwOnClientError && isFilterClientError(error)) {
      throw error;
    }

    console.error("[Filter Service] ❌ Error fetching filter options:", error?.message);
    return null;
  }
};

const getCachedFiltersForHeader = unstable_cache(
  async () => {
    return fetchFiltersFromAPI();
  },
  ["filter-data-header"],
  {
    revalidate: CACHE_REVALIDATION.FILTER_DATA,
    tags: [CACHE_TAGS.FILTER_DATA],
  }
);

export const filterService = {
  getFilters: async (
    collectionSlug?: string,
    options: FetchFiltersOptions = {}
  ) => {
    
    if (collectionSlug) {
      return fetchFiltersFromAPI(collectionSlug, options);
    }
    
    return getCachedFiltersForHeader();
  },
};
